import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthenticatedApi } from './useAuthenticatedApi';
import { useOrganization } from '../contexts/OrganizationContext';
import { apiEndpoints } from '../utils/api';
import { ApplicationListResponse } from '../types/application';

const REDIRECT_AFTER_AUTH_KEY = 'redirect_after_auth';

const shouldRedirectFromPath = (pathname: string): boolean =>
  pathname === '/' ||
  pathname === '/sign-up' ||
  pathname === '/repositories' ||
  pathname === '/organization-setup';

export function useDemoReturnRedirect(isAuthenticated: boolean): void {
  const navigate = useNavigate();
  const location = useLocation();
  const api = useAuthenticatedApi();
  const { currentOrganization, isLoading } = useOrganization();

  useEffect(() => {
    if (!isAuthenticated || isLoading || !currentOrganization?.is_demo) {
      return;
    }

    if (!shouldRedirectFromPath(location.pathname)) {
      return;
    }

    if (sessionStorage.getItem(REDIRECT_AFTER_AUTH_KEY)) {
      return;
    }

    let cancelled = false;
    const redirectToDemoApplication = async () => {
      try {
        const response = await api.get<ApplicationListResponse>(
          apiEndpoints.applications.list(currentOrganization.id)
        );
        if (cancelled) return;

        const application = response.applications?.[0];
        const target = application
          ? `/repositories/${application.id}?org=${currentOrganization.id}`
          : `/repositories?org=${currentOrganization.id}`;
        const current = `${location.pathname}${location.search}`;

        if (target !== current) {
          navigate(target, { replace: true });
        }
      } catch (error) {
        console.error('Failed to redirect to demo application', error);
      }
    };

    redirectToDemoApplication();

    return () => {
      cancelled = true;
    };
  }, [
    api,
    currentOrganization?.id,
    currentOrganization?.is_demo,
    isAuthenticated,
    isLoading,
    location.pathname,
    location.search,
    navigate,
  ]);
}
